import { Button } from "@/components/ui/button";
import { ArrowRight, Play, Heart, MessageCircle, Share2, Bookmark, Sparkles, Eye, Phone, Check, Send, Loader2 } from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSharedCountUp } from "@/hooks/useCountUp";
import { use3DTilt } from "@/hooks/use3DTilt";
import { VideoThumbnail } from "./VideoThumbnail";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { analytics } from "@/lib/analytics";
import { useLanguage } from "@/contexts/LanguageContext";

// Floating Icons Component - Responsive
import { FloatingIcons } from "@/components/ui/floating-icons";

interface HeroVideoData {
  id: string;
  title: string;
  description: string | null;
  industry: string;
  location: string;
  video_url: string;
  thumbnail_url: string | null;
  campaign_type: string | null;
  views: number;
  likes: number;
  comments: number;
  conversions: number;
  revenue: number;
  roi_multiplier: number;
  testimonial: string | null;
  conversion_type: string | null;
}

const formatNumber = (num: number) => {
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toString();
};

// Reduced desktop icons (6 instead of 12) with lower opacity
const heroDesktopIcons = [
  { emoji: '🚀', top: '15%', left: '10%', delay: '0s' },
  { emoji: '⛽', top: '25%', right: '12%', delay: '2s' },
  { emoji: '🔥', top: '45%', left: '8%', delay: '4s' },
  { emoji: '💨', top: '60%', right: '15%', delay: '1s' },
  { emoji: '✨', top: '35%', left: '15%', delay: '3s' },
  { emoji: '⚡', top: '70%', left: '12%', delay: '5s' },
];

// Reduced mobile icons (4 instead of 8) - less clutter
const heroMobileIcons = [
  { emoji: '🚀', top: '8%', left: '5%', delay: '0s' },
  { emoji: '⛽', top: '12%', right: '6%', delay: '1s' },
  { emoji: '🔥', top: '22%', right: '8%', delay: '2s' },
  { emoji: '✨', top: '18%', left: '10%', delay: '3s' },
];

// Live Activity Ticker Messages
const liveActivityMessages = [
  { emoji: '🔥', text: 'Café Berlin just got 28 bookings' },
  { emoji: '⚡', text: 'Barber Munich went viral' },
  { emoji: '💬', text: 'FitnessCo got 142 DMs' },
  { emoji: '📈', text: 'Hair Salon Frankfurt: +847 views' },
  { emoji: '🚀', text: 'Driving School: 19 new leads' },
];

// Mobile Live Activity Ticker Component
const MobileLiveActivity = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % liveActivityMessages.length);
    }, 4000); // Slowed from 3s to 4s for better readability
    return () => clearInterval(interval);
  }, []);

  const currentMessage = liveActivityMessages[currentIndex];

  return (
    <div className="relative inline-flex items-center gap-2 glass-morphism border border-neon-green/30 rounded-full px-3 py-1.5 text-xs shadow-lg overflow-hidden">
      {/* Live Pulse Dot */}
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-neon-green opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-neon-green"></span>
      </span>
      
      {/* Message with fade animation */}
      <span 
        key={currentIndex}
        className="text-white/90 font-medium animate-live-activity-fade"
      >
        {currentMessage.emoji} {currentMessage.text}
      </span>
    </div>
  );
};

// Hero Video Card with 3D tilt, spring heart animation, and smooth count-up
const HeroVideoCard = ({ video, index, onClick }: { 
  video: HeroVideoData; 
  index: number; 
  onClick: () => void;
}) => {
  const { elementRef, tiltStyle, handlers, isHovering } = use3DTilt({ maxTilt: 8, scale: 1.03 });
  const [heartPop, setHeartPop] = useState(false);
  const [displayLikes, setDisplayLikes] = useState(video.likes);
  const [likesFlash, setLikesFlash] = useState(false);
  const animationRef = useRef<number>();

  // Smooth count-up animation for likes
  const animateLikesTo = useCallback((target: number) => {
    const start = displayLikes;
    const startTime = performance.now();
    const duration = 300;

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const currentValue = Math.round(start + (target - start) * easeOut);
      setDisplayLikes(currentValue);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    animationRef.current = requestAnimationFrame(animate);
  }, [displayLikes]);

  const handleMouseEnter = () => {
    handlers.onMouseEnter();
    
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    // Trigger heart spring animation
    setHeartPop(true);
    setTimeout(() => setHeartPop(false), 600);
    
    // Animate likes increase
    const newLikes = video.likes + Math.floor(Math.random() * 3) + 1;
    if (prefersReducedMotion) {
      setDisplayLikes(newLikes);
    } else {
      animateLikesTo(newLikes);
    }
    setLikesFlash(true);
    setTimeout(() => setLikesFlash(false), 400);
  };

  const handleMouseLeave = () => {
    handlers.onMouseLeave();
    setDisplayLikes(video.likes);
  };

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={elementRef}
      className="group relative cursor-pointer"
      style={{ ...tiltStyle, animationDelay: `${index * 50}ms` }}
      onClick={onClick}
      onMouseMove={handlers.onMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      title="See all results ↓"
    >
      {/* Phone Mockup Frame */}
      <div className={`relative aspect-[9/16] rounded-3xl overflow-hidden glass-morphism border-2 transition-all duration-300 hover-lift shadow-lg ${
        isHovering ? 'border-neon-cyan/60 shadow-neon-cyan/30' : 'border-neon-purple/30 hover:border-neon-purple/60 hover:shadow-neon-purple'
      }`}>
        {/* Video Thumbnail */}
        <div className="absolute inset-0">
          <VideoThumbnail video={video} index={index} />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/80"></div>
        </div>

        {/* Header Bar - Social Media Style */}
        <div className="absolute top-0 left-0 right-0 p-3 flex items-center gap-2.5 z-10">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-neon-purple to-neon-pink flex items-center justify-center text-white text-sm font-bold shadow-lg">
            {video.title.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-bold text-white truncate">{video.title}</span>
              <span className="w-4 h-4 rounded-full bg-neon-cyan flex items-center justify-center flex-shrink-0">
                <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
              </span>
            </div>
            <span className="text-xs text-gray-400">{video.campaign_type || 'UGC Video'}</span>
          </div>
        </div>

        {/* UPLIFT BADGE - Top Left with Animated Glow */}
        <div className="absolute top-14 left-3 z-20">
          <div className={`uplift-badge flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold shadow-lg transition-all duration-300 ${
            isHovering ? 'scale-110 shadow-neon-green/50' : ''
          }`}>
            <span className="text-sm">📈</span>
            <span className="text-white">
              +{video.conversions} {video.conversion_type || 'Calls'}
            </span>
          </div>
        </div>

        {/* Metrics Badge - Right Side with Heart Animation */}
        <div className={`absolute top-14 right-3 bg-black/60 backdrop-blur-sm rounded-lg p-2 border border-neon-cyan/20 space-y-1 transition-all duration-300 z-10 ${
          isHovering ? 'shadow-lg shadow-neon-cyan/20' : ''
        }`}>
          <div className="flex items-center gap-1.5 text-xs text-white">
            <Eye className="w-3.5 h-3.5 text-gray-400" />
            <span className="font-bold">+{formatNumber(video.views)}</span>
          </div>
          
          {/* Heart row with spring animation */}
          <div className="flex items-center gap-1.5 text-xs text-white relative">
            {heartPop && (
              <span className="absolute left-0 w-3.5 h-3.5 rounded-full bg-pink-400/50 animate-heart-ping" />
            )}
            <Heart 
              className={`w-3.5 h-3.5 text-pink-400 relative z-10 ${heartPop ? 'animate-heart-spring' : ''} ${isHovering ? 'drop-shadow-[0_0_6px_rgba(236,72,153,0.6)]' : ''}`} 
              fill={isHovering ? 'currentColor' : 'none'}
            />
            <span className={`font-bold transition-all ${likesFlash ? 'animate-likes-flash' : ''} ${isHovering ? 'text-pink-400' : ''}`}>
              +{displayLikes}
            </span>
          </div>
          
          <div className="flex items-center gap-1.5 text-xs text-white">
            <MessageCircle className="w-3.5 h-3.5 text-cyan-400" />
            <span className="font-bold">+{video.comments}</span>
          </div>
        </div>

        {/* Play Button Overlay */}
        <div className={`absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300 flex items-center justify-center ${
          isHovering ? 'opacity-100' : 'opacity-0'
        }`}>
          <div className={`w-14 h-14 rounded-full glass-morphism flex items-center justify-center transition-all duration-300 ${
            isHovering ? 'animate-glow-pulse scale-110' : ''
          }`}>
            <Play className="w-6 h-6 text-neon-cyan ml-1" fill="currentColor" />
          </div>
          <div className="absolute bottom-20 left-1/2 -translate-x-1/2 whitespace-nowrap">
            <span className="text-xs font-semibold text-white/90 bg-black/50 px-3 py-1 rounded-full">
              See all results ↓
            </span>
          </div>
        </div>

        {/* Social Action Icons Row */}
        <div className="absolute bottom-[5rem] left-0 right-0 px-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Heart 
              className={`w-5 h-5 text-white cursor-pointer transition-all duration-200 ${
                isHovering ? 'fill-pink-500 text-pink-500 scale-110 drop-shadow-[0_0_8px_rgba(236,72,153,0.6)]' : 'hover:scale-110'
              }`}
            />
            <MessageCircle className="w-5 h-5 text-white cursor-pointer hover:text-neon-cyan hover:scale-110 transition-all duration-200" />
            <Send className="w-5 h-5 text-white cursor-pointer hover:text-neon-purple hover:scale-110 transition-all duration-200" />
          </div>
          <Bookmark className="w-5 h-5 text-white cursor-pointer hover:text-yellow-400 hover:scale-110 transition-all duration-200" />
        </div>

        {/* Bottom Info Overlay */}
        <div className="absolute bottom-0 left-0 right-0 glass-morphism p-3 backdrop-blur-xl">
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-white truncate">{video.title}</div>
              <div className="text-xs text-white/80 truncate">📍 {video.location}</div>
            </div>
            {video.revenue > 0 && (
              <div className="flex items-center gap-1 bg-neon-green/20 border border-neon-green/30 rounded-lg px-2 py-1">
                <span className="text-xs font-bold text-neon-green">+€{video.revenue}</span>
              </div>
            )}
          </div>
        </div>

        {/* Heart burst particles */}
        {heartPop && (
          <div className="absolute top-[70px] right-[18px] pointer-events-none">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <span 
                key={i} 
                className={`absolute text-[10px] hero-particle-${i}`}
              >
                ❤️
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Skeleton card for loading state
const VideoCardSkeleton = () => (
  <div className="relative aspect-[9/16] rounded-3xl overflow-hidden glass-morphism border-2 border-neon-purple/20">
    <Skeleton className="absolute inset-0" />
    <div className="absolute top-0 left-0 right-0 p-3 flex items-center gap-2.5">
      <Skeleton className="w-9 h-9 rounded-full" />
      <div className="flex-1 space-y-1.5">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
    <div className="absolute top-14 right-3 space-y-2">
      <Skeleton className="h-4 w-16" />
      <Skeleton className="h-4 w-14" />
      <Skeleton className="h-4 w-12" />
    </div>
    <div className="absolute bottom-0 left-0 right-0 p-3">
      <Skeleton className="h-4 w-full mb-1" />
      <Skeleton className="h-3 w-2/3" />
    </div>
  </div>
);

const Hero = () => {
  const [heroVideos, setHeroVideos] = useState<HeroVideoData[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  const handlePickYourPack = () => {
    analytics.ctaClicked('get_my_content', 'hero');
    window.location.href = "/order";
  };

  const handleCalculateROI = () => {
    const calcSection = document.getElementById('calculator');
    if (calcSection) {
      calcSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleScrollToSocialProof = () => {
    const socialProofSection = document.getElementById('social-proof');
    if (socialProofSection) {
      socialProofSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Fetch videos from database
  useEffect(() => {
    const fetchHeroVideos = async () => {
      try {
        const { data, error } = await supabase
          .from('portfolio_videos')
          .select('*')
          .order('order_index', { ascending: true })
          .limit(8);

        if (error) throw error;
        setHeroVideos(data || []);
      } catch (error) {
        console.error('Error loading hero videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchHeroVideos();
  }, []);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-10 sm:pt-16 md:pt-20 pb-2 sm:pb-6 md:pb-10 px-4">
      {/* Animated Background Orbs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] -translate-y-1/2 translate-x-1/4 bg-gradient-radial from-primary/15 to-transparent rounded-full animate-float-orb pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] translate-y-1/3 -translate-x-1/4 bg-gradient-radial from-neon-purple/10 to-transparent rounded-full animate-float-orb-reverse pointer-events-none" />
      
      {/* Mobile Rocket Launch Animation */}
      <div className="md:hidden absolute top-[8%] left-1/2 -translate-x-1/2 z-20 pointer-events-none">
        <div className="animate-rocket-launch-mobile text-3xl opacity-80">
          🚀
        </div>
      </div>
      
      {/* Floating Icons */}
      <FloatingIcons desktopIcons={heroDesktopIcons} mobileIcons={heroMobileIcons} />

      <div className="container mx-auto px-4 sm:px-6 py-8 md:py-20 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Mobile Live Activity Ticker - Above Badge */}
          <div className="md:hidden flex justify-center mb-3 animate-stagger-0">
            <MobileLiveActivity />
          </div>

          {/* Urgency Badge - Simplified on Mobile */}
          <div className="text-center mb-8 md:mb-8 animate-stagger-1">
            <div className="relative inline-flex items-center justify-center gap-1.5 sm:gap-2 glass-morphism border border-orange-500/40 rounded-full px-3 sm:px-5 py-2 sm:py-2.5 text-[11px] sm:text-sm shadow-lg shadow-orange-500/10 overflow-hidden animate-glow-pulse-orange">
              <span className="absolute inset-0 bg-gradient-radial from-orange-500/20 to-transparent animate-pulse" />
              <span className="relative z-10 text-sm sm:text-base animate-rocket-launch" style={{ animationDuration: '2s', animationIterationCount: 'infinite' }}>🚀</span>
              <span className="relative z-10 text-orange-300 font-semibold flex items-center gap-1">
                {/* Mobile: Shortened text */}
                <span className="sm:hidden">47 businesses</span>
                <span className="hidden sm:inline">47 businesses went from <span className="text-white/60">invisible</span></span>
                <ArrowRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-neon-cyan" />
                <span className="text-neon-green font-bold">visible</span>
                <span className="hidden sm:inline">this week</span>
              </span>
            </div>
          </div>

          {/* SEO Content Block - visible to search engines */}
          <p className="sr-only">
            Fuel72 is the best AI UGC video generator for businesses. Create UGC videos without filming. 
            Hire UGC creators with AI. Make professional marketing videos in 72 hours. 
            Boost your business visibility online with AI-generated content.
            Available in USA, Canada, Australia, Germany, Austria, Switzerland.
            UGC Videos erstellen mit AI. UGC kaufen. KI UGC Generator Deutschland.
            No content? No time to film? Inconsistent posting? We're your bridge to content.
            Get visible online. Get found. Boost your business with AI UGC videos.
          </p>

          {/* Headline with Gradient Subheadline - Mobile Optimized */}
          <div className="text-center mb-8 md:mb-8 space-y-4 md:space-y-4 px-2 animate-stagger-2">
            <h1 className="text-[2rem] leading-[2.3rem] sm:text-5xl md:text-6xl lg:text-7xl font-heading font-extrabold tracking-tight text-white">
              {t("hero.headline")}<br /><span className="text-neon-pink text-[1.7rem] sm:text-5xl md:text-6xl lg:text-7xl">{t("hero.headlineHighlight")}</span>
            </h1>
            <p className="text-lg sm:text-2xl md:text-3xl font-bold leading-snug bg-gradient-to-r from-neon-pink via-neon-cyan to-neon-purple bg-clip-text text-transparent animate-text-shimmer bg-[length:200%_100%]">
              {t("hero.subline")}
            </p>
            {/* Bridge to content messaging */}
            <p className="text-sm sm:text-base text-muted-foreground/90 max-w-xl mx-auto">
              <span className="text-white font-semibold">{t("hero.description")}</span>
            </p>
            <p className="hidden sm:block text-sm text-muted-foreground/70 max-w-lg mx-auto">
              {t("hero.descriptionHighlight")}
            </p>
          </div>

          {/* Primary CTA - Premium Dopamine Button */}
          <div className="flex justify-center mb-8 md:mb-8 animate-stagger-3 px-4">
            <Button 
              onClick={handlePickYourPack}
              size="lg"
              className="relative w-full sm:w-auto group overflow-hidden bg-gradient-to-r from-orange-500 via-neon-pink to-orange-500 bg-[length:200%_100%] hover:bg-[position:100%_0] text-white font-heading font-bold text-base sm:text-lg px-8 py-6 sm:py-7 rounded-xl shadow-2xl shadow-orange-500/40 transition-all duration-500 hover:scale-[1.03] hover:shadow-orange-500/60 hover:shadow-3xl active:scale-[0.97] animate-cta-glow"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
              <span className="relative flex items-center gap-2">
                <span className="relative">
                  <span className="inline-block group-hover:animate-fuel-pump">⛽</span>
                  <span className="fuel-drip-container">
                    <span className="fuel-drip fuel-drip-1"></span>
                    <span className="fuel-drip fuel-drip-2"></span>
                    <span className="fuel-drip fuel-drip-3"></span>
                  </span>
                </span>
                {t("hero.cta")}
                <ArrowRight className="w-5 h-5 transition-all duration-300 group-hover:translate-x-1.5 group-hover:scale-110" />
              </span>
            </Button>
          </div>

          {/* Proof Badge - Mobile Priority with Odometer Count-up */}
          <ProofBadge />

          {/* Secondary Action - Hidden on Mobile */}
          <div className="hidden sm:block text-center mb-6 md:mb-10 animate-stagger-5 px-4">
            <button 
              onClick={handleCalculateROI}
              className="group text-base font-semibold text-muted-foreground/80 hover:text-neon-cyan border-b-2 border-neon-cyan/50 hover:border-neon-cyan pb-1 transition-all duration-200 bg-transparent inline-flex items-center gap-1"
            >
              {t("hero.ctaSecondary")}
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </div>

          {/* Trust Text - Hidden on Mobile */}
          <p className="hidden sm:block text-center text-sm text-muted-foreground/70 mb-8 md:mb-12 animate-stagger-6 px-4 leading-relaxed">
            <strong className="text-muted-foreground/90">No filming. No editing. No learning AI tools.</strong>
            <span> </span>We do the research. We craft the concept. You just post.
          </p>

          {/* Video Carousel - Desktop Only with Dynamic Database Videos */}
          <div className="hidden md:block mb-8 md:mb-12 animate-stagger-7">
            {loading ? (
              <div className="grid grid-cols-4 gap-4 max-w-5xl mx-auto">
                {[1, 2, 3, 4].map((i) => (
                  <VideoCardSkeleton key={i} />
                ))}
              </div>
            ) : heroVideos.length > 0 ? (
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                }}
                className="w-full max-w-5xl mx-auto"
              >
                <CarouselContent className="-ml-2 md:-ml-4">
                  {heroVideos.map((video, index) => (
                    <CarouselItem key={video.id} className="pl-2 md:pl-4 basis-full sm:basis-1/2 lg:basis-1/3 xl:basis-1/4">
                      <HeroVideoCard 
                        video={video}
                        index={index}
                        onClick={handleScrollToSocialProof}
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="glass-morphism border-neon-purple/30 hover:border-neon-purple" />
                <CarouselNext className="glass-morphism border-neon-purple/30 hover:border-neon-purple" />
              </Carousel>
            ) : (
              <div className="text-center py-8 glass-morphism rounded-xl max-w-2xl mx-auto">
                <p className="text-muted-foreground">Upload portfolio videos in admin to showcase here!</p>
              </div>
            )}

            {/* Caption Below Carousel */}
            <p className="text-center text-sm text-muted-foreground mt-4 md:mt-6 max-w-2xl mx-auto px-4">
              <span className="text-foreground font-semibold">These are real videos. Real businesses. Real results.</span>
              <br />
              Watch how we hook, engage, and convert.
            </p>
          </div>

        </div>
      </div>

      <style>{`
        .glow-text {
          text-shadow: 0 0 20px hsl(var(--primary) / 0.5),
                       0 0 40px hsl(var(--primary) / 0.3);
        }

        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }

        @keyframes glowPulseOrange {
          0%, 100% { box-shadow: 0 4px 12px rgba(255, 107, 53, 0.15); }
          50% { box-shadow: 0 6px 24px rgba(255, 107, 53, 0.35), 0 0 40px rgba(255, 107, 53, 0.2); }
        }

        .animate-glow-pulse-orange {
          animation: glowPulseOrange 1.5s ease-in-out infinite;
        }

        /* Uplift Badge Styling */
        .uplift-badge {
          background: linear-gradient(135deg, rgba(34, 197, 94, 0.85), rgba(16, 185, 129, 0.9));
          border: 1px solid rgba(34, 197, 94, 0.6);
          backdrop-filter: blur(8px);
          animation: upliftGlow 2s ease-in-out infinite;
        }

        @keyframes upliftGlow {
          0%, 100% { box-shadow: 0 2px 8px rgba(34, 197, 94, 0.4), 0 0 12px rgba(34, 197, 94, 0.2); }
          50% { box-shadow: 0 4px 16px rgba(34, 197, 94, 0.6), 0 0 24px rgba(34, 197, 94, 0.4); }
        }

        /* Premium CTA Glow */
        @keyframes ctaGlow {
          0%, 100% { box-shadow: 0 10px 40px -10px rgba(236, 72, 153, 0.5), 0 0 20px rgba(236, 72, 153, 0.2); }
          50% { box-shadow: 0 15px 50px -10px rgba(236, 72, 153, 0.7), 0 0 30px rgba(236, 72, 153, 0.3); }
        }

        .animate-cta-glow {
          animation: ctaGlow 2.5s ease-in-out infinite;
        }

        /* Metric Pop Animation */
        @keyframes metricPop {
          0% { transform: scale(1); }
          50% { transform: scale(1.2); filter: brightness(1.3); }
          100% { transform: scale(1); filter: brightness(1); }
        }

        .animate-metric-pop {
          animation: metricPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        /* Sparkle Pop Animation */
        @keyframes sparklePop {
          0% { opacity: 0; transform: scale(0) rotate(0deg); }
          50% { opacity: 1; transform: scale(1.2) rotate(180deg); }
          100% { opacity: 0; transform: scale(0.5) rotate(360deg); }
        }

        .animate-sparkle-pop {
          animation: sparklePop 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }

        /* Badge Celebration */
        @keyframes badgeCelebration {
          0%, 100% { border-color: rgba(6, 182, 212, 0.5); box-shadow: 0 0 30px rgba(6, 182, 212, 0.3); }
          25% { border-color: rgba(168, 85, 247, 0.5); box-shadow: 0 0 35px rgba(168, 85, 247, 0.3); }
          50% { border-color: rgba(236, 72, 153, 0.5); box-shadow: 0 0 40px rgba(236, 72, 153, 0.3); }
          75% { border-color: rgba(34, 197, 94, 0.5); box-shadow: 0 0 35px rgba(34, 197, 94, 0.3); }
        }

        .animate-badge-celebration {
          animation: badgeCelebration 3s ease-in-out infinite;
        }

        /* Rainbow Sweep */
        @keyframes rainbowSweep {
          0% { background: linear-gradient(90deg, transparent, rgba(236, 72, 153, 0.3), transparent); background-position: -100% 0; }
          100% { background: linear-gradient(90deg, transparent, rgba(6, 182, 212, 0.3), transparent); background-position: 200% 0; }
        }

        .animate-rainbow-sweep {
          animation: rainbowSweep 2s ease-out;
          background-size: 50% 100%;
        }

        /* Heart Spring Animation */
        @keyframes heartSpring {
          0% { transform: scale(1); }
          15% { transform: scale(1.4); }
          30% { transform: scale(0.85); }
          45% { transform: scale(1.2); }
          60% { transform: scale(0.95); }
          75% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }

        .animate-heart-spring {
          animation: heartSpring 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        /* Heart Ping */
        @keyframes heartPing {
          0% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(2.5); opacity: 0; }
        }

        .animate-heart-ping {
          animation: heartPing 0.5s ease-out forwards;
        }

        /* Likes Flash */
        @keyframes likesFlash {
          0%, 100% { transform: scale(1); color: inherit; }
          50% { transform: scale(1.15); color: rgb(236, 72, 153); }
        }

        .animate-likes-flash {
          animation: likesFlash 0.4s ease-out;
        }

        /* Hero Particle Animations */
        @keyframes heroParticle1 { 0% { opacity: 1; transform: translate(0, 0) scale(1); } 100% { opacity: 0; transform: translate(-15px, -25px) scale(0); } }
        @keyframes heroParticle2 { 0% { opacity: 1; transform: translate(0, 0) scale(1); } 100% { opacity: 0; transform: translate(15px, -20px) scale(0); } }
        @keyframes heroParticle3 { 0% { opacity: 1; transform: translate(0, 0) scale(1); } 100% { opacity: 0; transform: translate(-20px, -10px) scale(0); } }
        @keyframes heroParticle4 { 0% { opacity: 1; transform: translate(0, 0) scale(1); } 100% { opacity: 0; transform: translate(20px, -15px) scale(0); } }
        @keyframes heroParticle5 { 0% { opacity: 1; transform: translate(0, 0) scale(1); } 100% { opacity: 0; transform: translate(-10px, -30px) scale(0); } }
        @keyframes heroParticle6 { 0% { opacity: 1; transform: translate(0, 0) scale(1); } 100% { opacity: 0; transform: translate(10px, -25px) scale(0); } }

        .hero-particle-1 { animation: heroParticle1 0.6s ease-out forwards; }
        .hero-particle-2 { animation: heroParticle2 0.6s ease-out forwards 0.05s; }
        .hero-particle-3 { animation: heroParticle3 0.6s ease-out forwards 0.1s; }
        .hero-particle-4 { animation: heroParticle4 0.6s ease-out forwards 0.15s; }
        .hero-particle-5 { animation: heroParticle5 0.6s ease-out forwards 0.2s; }
        .hero-particle-6 { animation: heroParticle6 0.6s ease-out forwards 0.25s; }

        @media (prefers-reduced-motion) {
          .animate-float, .animate-glow-pulse-orange, .uplift-badge, 
          .animate-cta-glow, .animate-metric-pop, .animate-sparkle-pop,
          .animate-badge-celebration, .animate-rainbow-sweep,
          .animate-heart-spring, .animate-heart-ping, .animate-likes-flash,
          .hero-particle-1, .hero-particle-2, .hero-particle-3,
          .hero-particle-4, .hero-particle-5, .hero-particle-6 {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
};

// Animated Metric Component with Premium Dopamine Effects
interface AnimatedMetricProps {
  value: number;
  suffix: string;
  labelLines: string[];
  gradient: string;
  isAnimating: boolean;
  hasCompleted: boolean;
  hasStarted: boolean;
  delay?: number;
}

const AnimatedMetric = ({ 
  value, 
  suffix, 
  labelLines, 
  gradient, 
  isAnimating, 
  hasCompleted,
  hasStarted,
  delay = 0
}: AnimatedMetricProps) => {
  const [showComplete, setShowComplete] = useState(false);
  const [showSparkle, setShowSparkle] = useState(false);

  useEffect(() => {
    if (hasCompleted && !showComplete) {
      setShowComplete(true);
      setShowSparkle(true);
      
      const sparkleTimer = setTimeout(() => setShowSparkle(false), 800);
      const completeTimer = setTimeout(() => setShowComplete(false), 600);
      
      return () => {
        clearTimeout(sparkleTimer);
        clearTimeout(completeTimer);
      };
    }
  }, [hasCompleted, showComplete]);

  return (
    <div 
      className="relative flex flex-col items-center gap-1 sm:gap-1.5 min-w-0"
      style={{ animationDelay: `${delay}ms` }}
    >
      {showSparkle && (
        <>
          <Sparkles className="absolute -top-2 -right-2 w-4 h-4 text-neon-cyan animate-sparkle-pop" />
          <Sparkles className="absolute -top-1 -left-3 w-3 h-3 text-neon-pink animate-sparkle-pop" style={{ animationDelay: '100ms' }} />
          <Sparkles className="absolute top-4 -right-3 w-3 h-3 text-neon-purple animate-sparkle-pop" style={{ animationDelay: '200ms' }} />
        </>
      )}
      
      <div className={`
        relative transition-all duration-300
        ${isAnimating ? 'scale-105' : ''}
        ${showComplete ? 'animate-metric-pop scale-110' : ''}
      `}>
        {isAnimating && (
          <div className="absolute inset-0 blur-lg bg-gradient-to-r from-neon-pink/40 to-neon-cyan/40 animate-pulse" />
        )}
        
        <span className={`
          relative text-xl sm:text-2xl md:text-3xl font-extrabold 
          bg-clip-text text-transparent ${gradient}
        `}>
          {value}{suffix}
        </span>
      </div>
      
      <div className="text-[11px] sm:text-xs text-muted-foreground/80 font-medium uppercase tracking-wider text-center leading-tight">
        {labelLines.map((line, i) => (
          <span key={i} className="block">
            {line}
          </span>
        ))}
      </div>
    </div>
  );
};

// Proof Badge Component with Staggered Odometer Animation
const ProofBadge = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [allComplete, setAllComplete] = useState(false);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [isVisible]);

  const views = useSharedCountUp(850, isVisible, { duration: 1500, delay: 0 });
  const bookings = useSharedCountUp(3.2, isVisible, { duration: 1500, decimals: 1, delay: 400 });
  const businesses = useSharedCountUp(250, isVisible, { duration: 1500, delay: 800 });

  const completedCount = [views.hasCompleted, bookings.hasCompleted, businesses.hasCompleted].filter(Boolean).length;
  
  useEffect(() => {
    if (completedCount === 3 && !allComplete) {
      setAllComplete(true);
    }
  }, [completedCount, allComplete]);

  return (
    <div className="mb-4 md:mb-8 px-4 sm:px-4 animate-stagger-4" ref={containerRef}>
      <div className={`
        relative glass-morphism border-2 rounded-2xl p-4 sm:p-5 md:p-6 
        backdrop-blur-xl shadow-xl overflow-hidden max-w-xs sm:max-w-md mx-auto
        transition-all duration-500 active:scale-[0.98]
        ${allComplete 
          ? 'border-neon-cyan/50 shadow-neon-cyan/30 animate-badge-celebration' 
          : completedCount > 0 
            ? 'border-neon-pink/40 shadow-neon-pink/20' 
            : 'border-neon-pink/30 shadow-neon-pink/10'
        }
      `}>
        <div className={`
          absolute -top-10 -right-10 w-40 h-40 bg-gradient-radial from-neon-cyan/20 to-transparent 
          rounded-full blur-2xl pointer-events-none transition-all duration-700
          ${completedCount > 1 ? 'opacity-100 scale-110' : completedCount > 0 ? 'opacity-70' : 'opacity-30'}
        `} />
        <div className={`
          absolute -bottom-10 -left-10 w-40 h-40 bg-gradient-radial from-neon-pink/20 to-transparent 
          rounded-full blur-2xl pointer-events-none transition-all duration-700
          ${completedCount > 2 ? 'opacity-100 scale-110' : completedCount > 0 ? 'opacity-50' : 'opacity-20'}
        `} />
        
        {allComplete && (
          <div className="absolute inset-0 pointer-events-none animate-rainbow-sweep opacity-30" />
        )}
        
        <div className="relative z-10 grid grid-cols-3 gap-4 sm:gap-4 md:gap-6">
          <AnimatedMetric
            value={views.value}
            suffix="K+"
            labelLines={['Views']}
            gradient="bg-gradient-to-br from-neon-purple to-neon-pink"
            isAnimating={views.isAnimating}
            hasCompleted={views.hasCompleted}
            hasStarted={views.hasStarted}
            delay={0}
          />
          <AnimatedMetric
            value={bookings.value}
            suffix="K+"
            labelLines={['Bookings']}
            gradient="bg-gradient-to-br from-neon-cyan to-neon-purple"
            isAnimating={bookings.isAnimating}
            hasCompleted={bookings.hasCompleted}
            hasStarted={bookings.hasStarted}
            delay={400}
          />
          <AnimatedMetric
            value={businesses.value}
            suffix="+"
            labelLines={['Visible']}
            gradient="bg-gradient-to-br from-neon-pink to-neon-cyan"
            isAnimating={businesses.isAnimating}
            hasCompleted={businesses.hasCompleted}
            hasStarted={businesses.hasStarted}
            delay={800}
          />
        </div>
      </div>
    </div>
  );
};

export default Hero;
